import React, { useEffect, useMemo, useState } from "react";
import { Accordion, AccordionItem, Button, Tag, Tile } from "@carbon/react";
import { CheckmarkFilled, WarningAltFilled } from "@carbon/icons-react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import i18next from "i18next";

import { OrderResponse, ORDER_TYPES, MaterialType } from "../../types";
import {
  allLinesPriced,
  CURRENCY_LIST,
  currencyFormatter,
  fileText,
  LinePriceInputs,
  linePriceTotals,
  offerCurrencyOf,
  linePriceInputsOf,
  linePricesPayload,
  linePricesTotal,
  pricedLinesOf,
} from "../../utils";
import TextField from "../carbon/Form/TextField";
import SelectField from "../carbon/Form/SelectField";
import TextareaField from "../carbon/Form/TextareaField";
import FileReader from "../FileReader/FileReader";
import LinePriceTable, { formatTotals } from "../LinePriceTable/LinePriceTable";
import ServiceAsked from "../ServiceAsked/ServiceAsked";
import "./materialOfferAccordion.scss";

const numberFromString = (value: unknown, originalValue: unknown) => {
  if (
    originalValue === "" ||
    originalValue === null ||
    originalValue === undefined
  ) {
    return undefined;
  }
  return Number.isNaN(value as number) ? NaN : (value as number);
};

const offerSchema = yup.object().shape({
  price: yup
    .number()
    .transform(numberFromString)
    .typeError(i18next.t("validations.price"))
    .required(i18next.t("validations.priceRequired"))
    .min(0.01, i18next.t("validations.priceGreaterThanZero")),
  currency: yup.string().required(i18next.t("validations.currencyRequired")),
  comment: yup.string().max(500, i18next.t("validations.commentMaxLength")),
  expireDate: yup.string().when("$orderExpireDate", {
    is: true,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    then: (schema: any) =>
      schema.required(i18next.t("validations.expireDateRequired")),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    otherwise: (schema: any) => schema.optional(),
  }),
});

interface MaterialOfferAccordionProps {
  order: OrderResponse;
  expanded: boolean;
  onChange: (panel: string | false) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  offerData: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onOfferComplete: (orderId: string, offerData: any) => void;
  onCancelOffer: (orderId: string) => void;
  /** True if this offer already exists and is read-only. */
  isExistingOffer?: boolean;
  // N10: per-item "No cotiza" for joint quotations. When provided, the supplier
  // can decline just this line (pre-send) and quote the rest, with undo.
  isDeclined?: boolean;
  onDecline?: () => void;
  onUndecline?: () => void;
}

const todayISO = () => new Date().toISOString().split("T")[0];

const MaterialOfferAccordion: React.FC<MaterialOfferAccordionProps> = ({
  order,
  expanded,
  onChange,
  offerData,
  onOfferComplete,
  onCancelOffer,
  isExistingOffer = false,
  isDeclined = false,
  onDecline,
  onUndecline,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const isReadOnly = isExistingOffer && !isEditing;
  const isService = order.material?.category?.type === MaterialType.SERVICES;
  const isInverse =
    order.type === ORDER_TYPES.INVERSE ||
    order.type === ORDER_TYPES.INVERSE_JOINT;

  // Per-item attachments the buyer carried from the solped line (order.itemFiles,
  // kept separate from the request-level general order.files). Shown behind a
  // "View details" toggle so they read as belonging to THIS item.
  const itemFiles = (order.itemFiles ?? []).filter((f) => f?.fileURL);

  // SCRUM-116: a service quoted by line is priced line by line, each line in its own currency;
  // the offer's price and currency follow its lines.
  const pricedLines = useMemo(() => pricedLinesOf(order), [order]);
  const pricedByLine = pricedLines.length > 0;
  const startingCurrency = offerData.currency || order.currency || "USD";
  const [linePrices, setLinePrices] = useState<LinePriceInputs>(() =>
    linePriceInputsOf(pricedLines, offerData.linePrices, startingCurrency)
  );
  const [linePricesTouched, setLinePricesTouched] = useState(false);

  const schemaWithInverseValidation = useMemo(() => {
    if (isInverse && order.initialPrice) {
      const priceLimitMessage = i18next.t("tenders.inverse.priceLimit", {
        price: currencyFormatter(order.initialPrice, order.currency || "USD"),
      });
      return offerSchema.shape({
        price: yup
          .number()
          .transform(numberFromString)
          .typeError(i18next.t("validations.price"))
          .required(i18next.t("validations.priceRequired"))
          .min(0.01, i18next.t("validations.priceGreaterThanZero"))
          .test({
            name: "max-price",
            message: priceLimitMessage,
            test: (value: number | undefined) =>
              !value || value <= order.initialPrice!,
          }),
      });
    }
    return offerSchema;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.type, order.initialPrice, order.currency]);

  const {
    control,
    handleSubmit,
    formState: { isValid },
    watch,
    setValue,
  } = useForm({
    resolver: yupResolver(schemaWithInverseValidation),
    mode: "onChange",
    context: { orderExpireDate: order.expireDate },
    defaultValues: {
      price: offerData.price || "",
      currency: offerData.currency || order.currency || "USD",
      comment: offerData.comment || "",
      expireDate: offerData.expireDate || "",
    },
  });

  const watchedValues = watch();

  useEffect(() => {
    if (offerData.price) {
      setValue("price", offerData.price);
      setValue("currency", offerData.currency);
      setValue("comment", offerData.comment);
      setValue("expireDate", offerData.expireDate);
    }
    setLinePrices(
      linePriceInputsOf(pricedLines, offerData.linePrices, startingCurrency)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerData, setValue]);

  const changeLines = (next: LinePriceInputs) => {
    setLinePrices(next);
    setLinePricesTouched(true);
    const total = linePricesTotal(pricedLines, next);
    setValue("currency", offerCurrencyOf(pricedLines, next));
    setValue("price", total > 0 ? String(total) : "", { shouldValidate: true });
  };
  const changeLinePrice = (code: string, unitPrice: string) =>
    changeLines({ ...linePrices, [code]: { ...linePrices[code], unitPrice } });
  const changeLineCurrency = (code: string, currency: string) =>
    changeLines({ ...linePrices, [code]: { ...linePrices[code], currency } });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSubmit = (data: any) => {
    onOfferComplete(order.id, {
      ...data,
      price: parseFloat(data.price),
      ...(pricedByLine
        ? { linePrices: linePricesPayload(pricedLines, linePrices) }
        : {}),
    });
    setIsEditing(false);
  };

  const isComplete = offerData.isComplete && !isEditing;
  const lineTotal =
    watchedValues.price &&
    currencyFormatter(
      Number(parseFloat(watchedValues.price) * order.quantity),
      watchedValues.currency
    );

  const headerTotal = !offerData.price
    ? ""
    : pricedByLine && offerData.linePrices?.length
      ? formatTotals(
          linePriceTotals(
            pricedLines,
            linePriceInputsOf(
              pricedLines,
              offerData.linePrices,
              startingCurrency
            )
          )
        )
      : currencyFormatter(
          Number((parseFloat(offerData.price) * order.quantity).toFixed(2)),
          offerData.currency
        );

  const accordionTitle = (
    <span className="offer-line__title">
      {isReadOnly || isComplete ? (
        <CheckmarkFilled className="offer-line__status offer-line__status--ok" />
      ) : (
        <WarningAltFilled className="offer-line__status offer-line__status--pending" />
      )}
      <span className="offer-line__title-text">
        <span className="offer-line__material">
          {order.material?.globalMaterialName}
          {isDeclined && (
            <Tag type="red" size="sm" className="offer-line__declined-tag">
              {i18next.t("offer.declineOffer.button")}
            </Tag>
          )}
        </span>
        {headerTotal && (
          <span className="offer-line__total">{headerTotal}</span>
        )}
      </span>
    </span>
  );

  return (
    <Tile className="offer-line">
      <Accordion className="offer-line__accordion">
        <AccordionItem
          open={expanded}
          onHeadingClick={() => onChange(expanded ? false : order.id)}
          title={accordionTitle}
        >
          {itemFiles.length > 0 && (
            <div className="offer-line__attachments">
              <Button
                kind="ghost"
                size="sm"
                onClick={() => setShowDetails((v) => !v)}
              >
                {i18next.t("cta.viewDetails")} ({itemFiles.length})
              </Button>
              {showDetails && (
                <div className="offer-line__attachment-files">
                  <p className="offer-line__attachment-label">
                    {i18next.t("offer.attachments")}
                  </p>
                  <div className="offer-line__file-list">
                    {itemFiles.map((file) => (
                      <FileReader
                        key={`${file.type}-${file.fileURL}`}
                        fileKey={file.fileURL}
                        text={fileText(file.type)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <dl className="offer-line__details">
            {!isService ? (
              <>
                <div className="offer-line__detail">
                  <dt>{i18next.t("tenders.quantity")}</dt>
                  <dd>
                    {order.quantity} {order.measurementUnit || "N/A"}
                  </dd>
                </div>
                <div className="offer-line__detail">
                  <dt>{i18next.t("material.deliveryFormat")}</dt>
                  <dd>{order.material?.deliveryFormat || "N/A"}</dd>
                </div>
                <div className="offer-line__detail">
                  <dt>{i18next.t("material.brand")}</dt>
                  <dd>{order.material?.brand || "N/A"}</dd>
                </div>
                <div className="offer-line__detail">
                  <dt>{i18next.t("newMaterialForm.details")}</dt>
                  <dd>{order.material?.details || "N/A"}</dd>
                </div>
                <div className="offer-line__detail">
                  <dt>{i18next.t("material.comments")}</dt>
                  <dd>{order.material?.comments || "N/A"}</dd>
                </div>
                {order.material?.fileURL && (
                  <div className="offer-line__detail">
                    <dt>{i18next.t("material.file")}</dt>
                    <dd>
                      <FileReader fileKey={order.material?.fileURL} />
                    </dd>
                  </div>
                )}
              </>
            ) : pricedByLine ? (
              // SCRUM-116: a service quoted by line shows what was asked here, beside its lines,
              // instead of in the "Servicios a cotizar" card above.
              <ServiceAsked order={order} />
            ) : (
              <>
                {Number(order?.quantity) > 0 && (
                  <div className="offer-line__detail">
                    <dt>{i18next.t("tenders.quantity")}</dt>
                    <dd>
                      {order?.quantity} {order.measurementUnit || "N/A"}
                    </dd>
                  </div>
                )}
                <div className="offer-line__detail">
                  <dt>{i18next.t("tenders.form.purpose")}</dt>
                  <dd>{order.purpose || "N/A"}</dd>
                </div>
                <div className="offer-line__detail">
                  <dt>{i18next.t("material.brand")}</dt>
                  <dd>{order?.brand || "N/A"}</dd>
                </div>
                <div className="offer-line__detail">
                  <dt>{i18next.t("newMaterialForm.details")}</dt>
                  <dd>{order.material?.details || "N/A"}</dd>
                </div>
                <div className="offer-line__detail">
                  <dt>{i18next.t("material.comments")}</dt>
                  <dd>{order.material?.comments || "N/A"}</dd>
                </div>
              </>
            )}
          </dl>

          <form onSubmit={handleSubmit(onSubmit)} className="offer-line__form">
            {pricedByLine && (
              <div className="offer-line__line-prices">
                {!isReadOnly && (
                  <p className="offer-line__hint">
                    {i18next.t("linePricing.hint")}
                  </p>
                )}
                <LinePriceTable
                  lines={pricedLines}
                  prices={linePrices}
                  onPriceChange={
                    isReadOnly || isComplete ? undefined : changeLinePrice
                  }
                  onCurrencyChange={changeLineCurrency}
                  showMissing={linePricesTouched}
                />
              </div>
            )}
            <div className="offer-line__fields">
              {/* A line-priced service takes its price and currency from its lines. */}
              {!pricedByLine && (
                <TextField
                  name="price"
                  control={control}
                  type="number"
                  step="0.01"
                  min="0"
                  max={isInverse ? order.initialPrice : undefined}
                  disabled={isReadOnly}
                  placeholder={i18next.t("tenders.price")}
                  label={`${i18next.t("offer.table.price")} (${
                    watchedValues.currency || order.currency
                  }${order.measurementUnit ? `/${order.measurementUnit}` : ""}) ${i18next.t(
                    "tenders.seller.noTax"
                  )}${
                    isInverse && order.initialPrice
                      ? ` — ${i18next.t("tenders.inverse.max")}: ${currencyFormatter(
                          order.initialPrice,
                          order.currency
                        )}`
                      : ""
                  }`}
                />
              )}

              {!pricedByLine && (
                <SelectField
                  name="currency"
                  control={control}
                  label={i18next.t("tenders.form.currency")}
                  disabled={isInverse || isReadOnly}
                  options={CURRENCY_LIST.map((currency) => ({
                    value: currency.name,
                    label: currency.name,
                  }))}
                />
              )}

              {order.expireDate && (
                <TextField
                  name="expireDate"
                  control={control}
                  type="date"
                  min={todayISO()}
                  disabled={isReadOnly}
                  label={i18next.t("offer.table.expireDate")}
                />
              )}
            </div>

            <TextareaField
              name="comment"
              control={control}
              rows={3}
              disabled={isReadOnly}
              label={i18next.t("tenders.form.comments")}
              placeholder={i18next.t("tenders.form.comments")}
            />

            <div className="offer-line__form-footer">
              {watchedValues.price && !pricedByLine ? (
                <p className="offer-line__running-total">
                  {watchedValues.price} {watchedValues.currency} ×{" "}
                  {order.quantity} {order.measurementUnit} ={" "}
                  <strong>{lineTotal}</strong>
                </p>
              ) : (
                <span />
              )}

              <div className="offer-line__actions">
                {isReadOnly && (
                  <span className="offer-line__already">
                    <CheckmarkFilled className="offer-line__status offer-line__status--ok" />
                    {i18next.t("offer.alreadyOffered")}
                  </span>
                )}
                {/* N10: per-item decline / undo for joint quotations. */}
                {isDeclined && onUndecline && (
                  <Button kind="tertiary" onClick={onUndecline}>
                    {i18next.t("offer.declineOffer.undo")}
                  </Button>
                )}
                {!isDeclined && onDecline && !isReadOnly && !isComplete && (
                  <Button kind="tertiary" onClick={onDecline}>
                    {i18next.t("offer.declineOffer.button")}
                  </Button>
                )}
                {!isComplete && !isReadOnly && !isDeclined && (
                  <Button
                    type="submit"
                    disabled={
                      !isValid ||
                      !watchedValues.price ||
                      (pricedByLine && !allLinesPriced(pricedLines, linePrices))
                    }
                  >
                    {i18next.t("cta.confirm")}
                  </Button>
                )}
                {isComplete && !isExistingOffer && (
                  <Button
                    kind="tertiary"
                    onClick={() => {
                      onCancelOffer(order.id);
                      setIsEditing(false);
                    }}
                  >
                    {i18next.t("cta.cancel")}
                  </Button>
                )}
              </div>
            </div>
          </form>
        </AccordionItem>
      </Accordion>
    </Tile>
  );
};

export default MaterialOfferAccordion;
